import {
  Box,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  SvgIcon,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  debounce,
} from '@mui/material';
import {
  AppFormikButtonTiles,
  AppFormikTextField,
  AppPrimaryButton,
  AppPrimaryButtonOutlined,
} from 'shared/components/AppComponents';
import { ReactComponent as Search } from 'assets/images/ic_search.svg';
import { ReactComponent as Plus } from 'assets/images/ic_plus.svg';
import { Formik } from 'formik';
import EmptyPlaceholder from 'shared/components/EmptyPlaceholder';
import { useNavigate } from 'react-router-dom';
import DateFnsAdapter from '@date-io/date-fns';
import ReactTimeAgo from 'react-time-ago';
import { IPolicy } from '../Main';
import { useDispatch, useSelector } from 'react-redux';
import { IAppState } from 'state/RootReducer';
import { PaginationActions } from 'state/pagination/Action';
import { useCallback } from 'react';
import { BaseApplicationState } from 'state/baseapplication/Reducer';

const dateFns = new DateFnsAdapter();

const PolicyList: React.FC<PolicyListProps> = (props: any) => {
  const navigate = useNavigate();
  const { policyList, isLoading } = props;

  const dispatch = useDispatch();
  const { orgACLConfig }: BaseApplicationState = useSelector(
    (state: any) => state.baseAppState
  );
  const policyPagination = useSelector(
    (state: IAppState) => state.paginationState.policy
  );

  // Instance method
  const getReviewDate = (row: IPolicy): any => {
    if (row.needsReview) {
      return <ReactTimeAgo date={dateFns.date(row.lastReviewedDate)} />;
    }
    return <Typography>Not Scheduled</Typography>;
  };

  const getEffectiveDate = (row: IPolicy): any => {
    if (row.publishedDate) {
      return <ReactTimeAgo date={dateFns.date(row.publishedDate)} />;
    }
    return <Typography>Not Set</Typography>;
  };

  const handlePageChange = (_event: unknown, page: number): void => {
    dispatch(
      PaginationActions.setPagination({
        policy: {
          offset: page + 1,
        },
      })
    );
  };

  const debouncSearch = useCallback(
    debounce((searchValue: any) => {
      dispatch(
        PaginationActions.setPagination({
          policy: {
            ...policyPagination,
            offset: 1,
            search: searchValue,
          },
        })
      );
    }, 500),
    [policyPagination]
  );

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const pageLimit = Number(event.target.value);

    dispatch(
      PaginationActions.setPagination({
        policy: {
          offset: 1,
          pageLimit,
        },
      })
    );
  };

  const handleTabChange = (tabValue: string): void => {
    dispatch(
      PaginationActions.setPagination({
        policy: {
          ...policyPagination,
          offset: 1,
          tab: tabValue,
        },
      })
    );
  };

  const PolicySections = [
    {
      value: 'All Policies',
      tooltip:
        'View a comprehensive list of all organizational policies across all statuses and lifecycle stages.',
      onClick: () => handleTabChange('All Policies'),
    },
    {
      value: 'Pending Review',
      tooltip:
        'Filter policies currently awaiting governance evaluation, stakeholder feedback, or approval sign-offs.',
      onClick: () => handleTabChange('Pending Review'),
    },
    {
      value: 'Published',
      tooltip:
        'Access all active, officially enforced policies currently applied across the organization.',
      onClick: () => handleTabChange('Published'),
    },
    {
      value: 'Draft',
      tooltip:
        'View work-in-progress policies currently being authored or updated before official submission.',
      onClick: () => handleTabChange('Draft'),
    },
    {
      value: 'Archived',
      tooltip:
        'Review outdated, retired, or superseded policies retained for historical and audit reference.',
      onClick: () => handleTabChange('Archived'),
    },
  ];

  // UI Elements
  return (
    <Box className="policy-wrapper">
      <Stack className="policy-container" direction="column">
        <Stack direction="row" justifyContent="flex-end" spacing={2}>
          <AppPrimaryButtonOutlined
            title="Advanced search"
            startIcon={
              <SvgIcon
                component={Search}
                inheritViewBox
                className="view-icon"
                fontSize="inherit"
                htmlColor="#76A81F"
              />
            }
            sx={{ opacity: 0 }}
          />

          {orgACLConfig.policies?.includes(
            'policies.policyManagement.createNewPolicy'
          ) && (
            <AppPrimaryButton
              title="Create new policy"
              startIcon={
                <SvgIcon
                  component={Plus}
                  inheritViewBox
                  className="view-icon"
                  fontSize="inherit"
                  htmlColor="#76A81F"
                />
              }
              onClick={() => navigate('/policy-management/add')}
            />
          )}
        </Stack>

        <Formik
          enableReinitialize
          initialValues={{
            'search-policy': policyPagination.search,
            status: Array.from(
              { length: 5 },
              (a, i) => PolicySections[i].value === policyPagination.tab
            ),
          }}
          validateOnMount
          onSubmit={() => console.log('test')}
        >
          {() => (
            <Paper variant="outlined">
              <Grid container className="policy-list-header">
                <Grid item sm={8} xs={12}>
                  <Box className="policy-title">
                    <Typography variant="h4">Policies</Typography>
                    <Typography component="span">
                      View and manage all organisational policies
                    </Typography>
                  </Box>
                </Grid>
                <Grid item sm={4} xs={12}>
                  <Box className="policies-search">
                    <AppFormikTextField
                      inputProps={{
                        startAdornment: (
                          <SvgIcon
                            component={Search}
                            inheritViewBox
                            className="view-icon"
                            fontSize="inherit"
                            htmlColor="#76A81F"
                          />
                        ),
                      }}
                      outerLabel={false}
                      placeholder="Search policies.."
                      name="search-policy"
                      onChange={(e: any) => {
                        debouncSearch(e.target.value);
                      }}
                    />
                  </Box>
                </Grid>
              </Grid>

              <Box>
                <AppFormikButtonTiles
                  variant="outlined"
                  name="status"
                  size="small"
                  label=""
                  disabled={false}
                  multichoice={false}
                  className="app-group-button"
                  buttons={PolicySections}
                  outerLabel
                />
                {isLoading && (
                  <Box className="empty-content">
                    <CircularProgress />
                  </Box>
                )}
                {!isLoading && (
                  <TableContainer>
                    <Table className="app-table">
                      <TableHead>
                        <TableRow>
                          <TableCell>Title</TableCell>
                          <TableCell>Version</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Acknowledgement</TableCell>
                          <TableCell>Review date</TableCell>
                          <TableCell>Owner</TableCell>
                          <TableCell>Effective date</TableCell>
                          {/* <TableCell>Coverage</TableCell> */}
                          <TableCell>Last updated</TableCell>
                          <TableCell>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {policyList.map((row: any) => (
                          <TableRow
                            key={row._id}
                            sx={{
                              '& .MuiTableCell-root': { whiteSpace: 'nowrap' },
                            }}
                          >
                            <TableCell className="word-break">
                              {row.title}
                            </TableCell>
                            <TableCell>{row.version}</TableCell>
                            <TableCell>{row.status}</TableCell>
                            <TableCell>
                              {row.acknowledgementRequired ? (
                                <Chip size="small" label="Required" />
                              ) : (
                                <Chip size="small" label="Not Required" />
                              )}
                            </TableCell>
                            <TableCell>{getReviewDate(row)}</TableCell>
                            <TableCell className="word-break">
                              {row.owner}
                            </TableCell>
                            <TableCell>{getEffectiveDate(row)}</TableCell>
                            {/* <TableCell>{row.coverage}</TableCell> */}
                            <TableCell>
                              {row.updatedAt
                                ? dateFns.date(row.updatedAt).toDateString()
                                : dateFns.date(row.createdAt).toDateString()}
                            </TableCell>
                            <TableCell>
                              {orgACLConfig.policies?.includes(
                                'policies.policyManagement.view'
                              ) && (
                                <AppPrimaryButtonOutlined
                                  title="View"
                                  isLoading={false}
                                  disabled={false}
                                  className="primary-light-fill btn-extra-small"
                                  onClick={() => {
                                    navigate(
                                      `/policy-management/detail/${row._id}`
                                    );
                                  }}
                                />
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
                {policyList.length === 0 && !isLoading && (
                  <EmptyPlaceholder title="No policies found" />
                )}
              </Box>
            </Paper>
          )}
        </Formik>
      </Stack>

      {!isLoading && policyList.length > 0 && (
        <TablePagination
          component="div"
          count={policyPagination.totalRecords}
          page={policyPagination.offset - 1}
          rowsPerPage={policyPagination.pageLimit}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={[10, 20, 50]}
          disabled={isLoading}
        />
      )}
    </Box>
  );
};

export default PolicyList;
